import {WebCommenting} from '@/client-lazy'
import type {SiteDocumentPayload} from '@/loaders'
import {NoSitePage, NotRegisteredPage} from '@/not-registered'
import {WebSiteProvider} from '@/providers'
import {unwrap} from '@/wrapping'
import {WebResourcePage} from '@/web-resource-page'
import {Code} from '@connectrpc/connect'
import {Params, useLoaderData} from '@remix-run/react'
import {UnpackedHypermediaId} from '@seed-hypermedia/client/hm-types'
import {createDocumentNavRoute, ViewRouteKey} from '@shm/shared'
import {DaemonErrorPage, loader as loaderFn, meta as metaFn} from './$'
import {shouldRevalidateDocumentRoute} from './revalidation'

export const loader = async ({params, request}: {params: Params; request: Request}) => {
  return await loaderFn({
    params,
    request,
  })
}

export function shouldRevalidate({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: {
  currentUrl: URL
  nextUrl: URL
  defaultShouldRevalidate: boolean
}) {
  return shouldRevalidateDocumentRoute({currentUrl, nextUrl, defaultShouldRevalidate})
}

type ExtendedSitePayload = SiteDocumentPayload & {
  viewTerm?: ViewRouteKey | null
  panelParam?: string | null // Supports extended format like "comments/BLOCKID" or "comments/COMMENT_ID"
  replyCommentId?: string | null
  openComment?: string | null
}

type DocumentPayload = ExtendedSitePayload | 'unregistered' | 'no-site'

export default function IndexPage() {
  const unwrappedData = useLoaderData()
  const data = unwrap<DocumentPayload>(unwrappedData)

  if (data === 'unregistered') {
    return <NotRegisteredPage />
  }
  if (data === 'no-site') {
    return <NoSitePage />
  }

  // Handle errors
  if (data.daemonError && data.daemonError.code !== Code.NotFound && data.daemonError.code !== Code.PermissionDenied) {
    return <DaemonErrorPage message={data.daemonError.message} code={data.daemonError.code} />
  }

  // Render unified ResourcePage with WebSiteProvider for navigation context
  return (
    <WebSiteProvider
      origin={data.origin}
      originHomeId={data.originHomeId}
      siteHost={data.siteHost}
      dehydratedState={data.dehydratedState}
      initialRoute={createDocumentNavRoute(
        data.id,
        data.viewTerm,
        data.panelParam,
        data.openComment,
        undefined,
        undefined,
        data.replyCommentId,
      )}
    >
      <InnerResourcePage docId={data.id} ssrContentHTML={data.ssrContentHTML} />
    </WebSiteProvider>
  )
}

/** Inner component that can use hooks after providers are mounted */
function InnerResourcePage({docId, ssrContentHTML}: {docId: UnpackedHypermediaId; ssrContentHTML?: string | null}) {
  return <WebResourcePage docId={docId} CommentEditor={WebCommenting} ssrContentHTML={ssrContentHTML} />
}

export const meta = metaFn
